// Copyright (c) 2020-2022 Visual Intelligence LP. All rights reserved.
//
import { action, makeObservable, observable } from 'mobx';

import { DeliverableDTO } from '@shared/models/deliverable';
import { AssetType } from '@shared/constants/asset';
import { AssetDTO } from '@shared/models/asset';
import { OrderType, OrderStatus } from '@shared/constants/order';
import { CustomerDTO } from '@shared/models/customers';
import { JobRequestDTO } from '@shared/models/job-request';
import { OrderTask } from '@shared/models/ordertask';
import { ScheduleInfoDTO } from './schedule';

export interface OrderAssetDTO {
  assetId: AssetDTO['id'];
  type?: AssetType;
  serialNumber: AssetDTO['serialNumber'];
  name: AssetDTO['name'];
  deliverables: Array<DeliverableDTO>;
  jobRequests?: Array<JobRequestDTO>;
  twinID: number;
  inviteCode: string;
  //variance: number; // replaced by ScheduleInfo.
  scheduleInfo: ScheduleInfoDTO;
}

export interface OrderDTO {
  id: number;
  orderNumber: string;
  orderName: string;
  type: OrderType;
  status: OrderStatus;
  dueDate: string;
  customerName: CustomerDTO['name'];
  customerId: CustomerDTO['id'];
  isBlocked?: boolean;
  isActive?: boolean;
  assets?: Array<OrderAssetDTO>;
  notes?: string;
  expectedDueDate?: string;
  actualDueDate: string;
  orderTasks: Array<OrderTask>;
}

export default class Order {
  id: number;
  orderNumber: string;
  @observable orderName: string;
  @observable type: OrderType;
  @observable status: OrderStatus;
  @observable dueDate: string;
  @observable expectedDueDate: string;
  @observable actualDueDate: string;
  customerName: string;
  customerId: CustomerDTO['id'];
  @observable isBlocked: boolean;
  @observable isActive: boolean;
  @observable assets: Array<OrderAssetDTO>;
  @observable notes: string;
  orderTasks: Array<OrderTask>; // TBD: need to be @observable?
  
  constructor(dto: OrderDTO) {
    this.update(dto);
    makeObservable(this);
  }

  get asJson(): OrderDTO {
    return {
      id: this.id,
      orderNumber: this.orderNumber,
      orderName: this.orderName,
      type: this.type,
      status: this.status,
      dueDate: this.dueDate,
      expectedDueDate: this.expectedDueDate,
      actualDueDate: this.actualDueDate,
      customerName: this.customerName,
      customerId: this.customerId,
      isBlocked: this.isBlocked,
      isActive: this.isActive,
      assets: this.assets,
      notes: this.notes,
      orderTasks: this.orderTasks
    };
  }

  get asJsonNoAsset(): OrderDTO {
    return {
      id: this.id,
      orderNumber: this.orderNumber,
      orderName: this.orderName,
      type: this.type,
      status: this.status,
      dueDate: this.dueDate,
      expectedDueDate: this.expectedDueDate,
      actualDueDate: this.actualDueDate,
      customerName: this.customerName,
      customerId: this.customerId,
      isBlocked: this.isBlocked,
      isActive: this.isActive,
      notes: this.notes,
      orderTasks: this.orderTasks
    };
  }

  @action update(newData: Partial<OrderDTO>): OrderDTO {
    const allData = { ...this.asJson, ...newData };

    Object.assign(this, allData);

    return this.asJson;
  }
}